import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute, RouterEvent } from '@angular/router';
import { filter, map, mergeMap, tap, distinctUntilChanged, mergeAll, merge, concat, concatAll, concatMapTo, } from 'rxjs/operators';
import { forEach, get, isNull, chain, cloneDeep } from 'lodash';
import { HeaderService, HelpersService, UserService } from '@base/shared/services';
import { RouterParams } from '@eyrus/shared/services';
import { forkJoin, of, combineLatest } from 'rxjs';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'eyrus-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.sass']
})
export class BreadcrumbsComponent implements OnInit {

  breadcrumbs: any[] = [];

  activeRoute: any = {};
  paramsData: any = {};

  private subscriptions: any = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private headerService: HeaderService,
    private routerParams: RouterParams,
    private helpers: HelpersService,
    private account: UserService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    const _params = this.routerParams.params;
    const _breadcrumbs = this.headerService.breadcrumbsListener;

    this.subscriptions.breadcrumbs = combineLatest(_params, _breadcrumbs)
      .pipe(
        map(([params, breadcrumbs]) => {
          // combineLatest returns an array of values, here we map those values to an object
          return { ...params, ...breadcrumbs };
        })
      )
      .subscribe(res => {
        this.paramsData = res;
        this.listenChanges(res);
      });
  }

  ngOnDestroy() {
    forEach(this.subscriptions, (value) => value.unsubscribe());
  }

  /**
   * Navigate to selected breadcrumb
   * @param item Selected breadcrumb
   * @param current A boolean to indicate if is current active tab
   */
  openBreadCrumb(item, current) {
    if (current)
      return;

    if (item.url)
      return this.router.navigate([item.url]);

    const url = this.makePath(item.path);
    const queryParams = item.params ? item.params : {};

    this.router.navigate([url], {
      queryParamsHandling: 'preserve',
      queryParams: { ...queryParams }
    });
  }

  /**
   * Create a new url to navigate to
   * @param path new path
   */
  private makePath(path) {
    let newPath = path;
    const replaceItems = chain(path.split('/'))
      .map(item => {
        const _label = item.replace(':', '');
        const data = {
          label: item,
          value: this.activeRoute[_label]
        };

        return item.startsWith(':') ? data : null;
      })
      .filter(item => item)
      .value();

    replaceItems.forEach(item => {
      newPath = newPath.replace(item.label, item.value);
    });

    return newPath;
  }

  /**
   * Listen to new changes
   * @param event data event
   */
  private listenChanges(event) {
    this.activeRoute = event;
    this.makeBreadCrumbs(get(cloneDeep(event), 'breadcrumbs') || []);
  }

  /**
   * Format breadcrumbs labels
   * @param data array of breadcrumbs
   */
  private makeBreadCrumbs(data) {
    const _breadcrumbs = chain(data)
      .map(breadcrumb => {

        if (!breadcrumb.label.startsWith(':'))
          return breadcrumb;
        if (breadcrumb.label.toLowerCase().indexOf('date') !== -1) {
          const _label = this.paramsData[breadcrumb.label.replace(':', '')];
          breadcrumb.label = moment(_label, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm'])
            .locale(this.helpers.getLang())
            .format('dddd, MMM DD YYYY');
        }

        if (breadcrumb.label.toLowerCase().indexOf('readerserial') !== -1) {
          const _label = this.activeRoute.typeOfView;
          breadcrumb.label = _label;
        }

        if (breadcrumb.label.toLowerCase().indexOf('projectname') !== -1) {
          const _label = get(this.account.account, 'project.projectName') || '--';
          breadcrumb.label = _label;
        }
        if (breadcrumb.label.toLowerCase().indexOf('companyname') !== -1) {
          const _label = get(this.account.account, 'contract.company.companyName') || '--';
          breadcrumb.label = _label;
        }
        if (breadcrumb.label.toLowerCase().indexOf('walkthroughname') !== -1) {
          const _label = get(this.account.account, 'contractWalkthrough.walkthroughName') || '--';
          breadcrumb.label = _label;
        }

        if (breadcrumb.label.toLowerCase().indexOf('queryparams') !== -1) {
          const params = breadcrumb.label.split(':').filter(item => item.length);
          const _label = this.route.snapshot[params[0]][params[1]];

          if (_label)
            breadcrumb.label = `common.${_label}`;
          else
            breadcrumb.label = 'empty';
        }

        return breadcrumb;
      })
      .value();

    this.breadcrumbs = _breadcrumbs;

    this.makeTitle();
  }

  private makeTitle() {
    const title = chain(this.breadcrumbs)
      .filter(item => item.inTitle)
      .map(item => this.translate.instant(item.label))
      .join(' - ')
      .value();

    if (title)
      this.headerService.tabTitleSet.next(title);
  }
}
